import React, { useState } from 'react';
import {
  FaFilePdf, FaFileWord, FaFileImage, FaFile,
  FaEye, FaDownload, FaTimes,
} from 'react-icons/fa';
import { getFileUrl } from '../services/api';

const getFileInfo = (url, filename) => {
  const name = filename || url?.split('/').pop() || 'attachment';
  const ext  = name.split('.').pop().toLowerCase();
  const fullUrl = getFileUrl(url);

  const isImage = ['jpg','jpeg','png','gif','webp','bmp'].includes(ext);
  const isPdf   = ext === 'pdf';
  const isWord  = ['doc','docx'].includes(ext);

  let Icon  = FaFile;
  let color = '#64748b';
  let label = 'File';
  if (isPdf)   { Icon = FaFilePdf;   color = '#dc2626'; label = 'PDF Document'; }
  if (isWord)  { Icon = FaFileWord;  color = '#1d4ed8'; label = 'Word Document'; }
  if (isImage) { Icon = FaFileImage; color = '#059669'; label = 'Image'; }

  return { name, ext, fullUrl, isImage, isPdf, isWord, Icon, color, label };
};

// Download a file via fetch → blob → invisible <a> click
const downloadBlob = async (fullUrl, name) => {
  try {
    const token = localStorage.getItem('token');
    const res   = await fetch(fullUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(href);
  } catch {
    // Fallback: force-navigate with ?download hint
    window.open(fullUrl, '_blank');
  }
};

// Open a file (e.g. PDF) in a new tab via fetch → blob, so the Authorization
// header reaches auth-gated routes too (plain window.open sends no headers).
const openBlob = async (fullUrl) => {
  try {
    const token = localStorage.getItem('token');
    const res   = await fetch(fullUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('fetch failed');
    const blob = await res.blob();
    const href = URL.createObjectURL(blob);
    window.open(href, '_blank');
  } catch {
    window.open(fullUrl, '_blank');
  }
};

/**
 * Displays an attachment with View + Download buttons.
 *
 * Props:
 *   url      — file path from the DB, e.g. "/uploads/123456.pdf"
 *   filename — original filename (optional, falls back to last URL segment)
 *   compact  — render as a single-row chip instead of a card (default false)
 *   onRemove — optional callback; when provided, shows a remove (×) button
 */
const FileAttachment = ({ url, filename, compact = false, onRemove }) => {
  const [imgError, setImgError]   = useState(false);
  const [lightbox, setLightbox]   = useState(false);

  if (!url) return null;

  const { name, fullUrl, isImage, isPdf, Icon, color, label } = getFileInfo(url, filename);
  const canPreview = isImage || isPdf;

  if (compact) {
    // Inline chip style — used inside tables / small cards
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: `${color}12`, borderRadius: 8,
                    padding: '5px 10px', border: `1px solid ${color}30` }}>
        <Icon size={14} color={color} />
        <span style={{ fontSize: 12, color, fontWeight: 600, maxWidth: 120,
                       overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={name}>{name}</span>
        {canPreview && (
          <button onClick={() => isImage ? setLightbox(true) : openBlob(fullUrl)}
                  title="View" style={btnStyle('#e0f2fe', '#0369a1')}>
            <FaEye size={11} />
          </button>
        )}
        <button onClick={() => downloadBlob(fullUrl, name)}
                title="Download" style={btnStyle('#dcfce7', '#15803d')}>
          <FaDownload size={11} />
        </button>
        {onRemove && (
          <button type="button" onClick={onRemove}
                  title="Remove" style={btnStyle('#fee2e2', '#dc2626')}>
            <FaTimes size={11} />
          </button>
        )}
        {lightbox && <Lightbox src={fullUrl} name={name} onClose={() => setLightbox(false)} />}
      </div>
    );
  }

  // Card style — used in detail / expanded views
  return (
    <div style={{
      border: `1.5px solid ${color}30`, borderRadius: 12,
      background: `${color}08`, overflow: 'hidden',
    }}>
      {/* Image thumbnail row */}
      {isImage && !imgError && (
        <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setLightbox(true)}>
          <img
            src={fullUrl}
            alt={name}
            onError={() => setImgError(true)}
            style={{ width: '100%', maxHeight: 200, objectFit: 'cover', display: 'block' }}
          />
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.2s',
          }}
               onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.25)'}
               onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}
          >
            <FaEye size={28} color="white" style={{ opacity: 0, transition: 'opacity 0.2s' }}
                   onMouseEnter={e => e.currentTarget.style.opacity = 1}
            />
          </div>
        </div>
      )}

      {/* File info + action row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px' }}>
        <div style={{
          width: 38, height: 38, borderRadius: 8,
          background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={18} color={color} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-dark)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
               title={name}>{name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 2 }}>{label}</div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
          {canPreview && (
            <button
              onClick={() => isImage ? setLightbox(true) : openBlob(fullUrl)}
              title={isImage ? 'View image' : 'Open PDF'}
              style={btnStyle('#dbeafe', '#1d4ed8')}
            >
              <FaEye size={13} />
              <span style={{ fontSize: 12, fontWeight: 600 }}>View</span>
            </button>
          )}
          <button
            onClick={() => downloadBlob(fullUrl, name)}
            title="Download file"
            style={btnStyle('#dcfce7', '#15803d')}
          >
            <FaDownload size={13} />
            <span style={{ fontSize: 12, fontWeight: 600 }}>Download</span>
          </button>
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              title="Remove file"
              style={btnStyle('#fee2e2', '#dc2626')}
            >
              <FaTimes size={13} />
            </button>
          )}
        </div>
      </div>

      {lightbox && <Lightbox src={fullUrl} name={name} onClose={() => setLightbox(false)} />}
    </div>
  );
};

// Shared button style helper
const btnStyle = (bg, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '6px 10px', borderRadius: 8, border: 'none', cursor: 'pointer',
  background: bg, color,
});

// Full-screen image lightbox
const Lightbox = ({ src, name, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
      zIndex: 9999, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: 16,
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  width: '100%', maxWidth: 900, marginBottom: 10 }}>
      <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>{name}</span>
      <button onClick={onClose}
              style={{ background: 'rgba(255,255,255,0.15)', border: 'none',
                       color: 'white', borderRadius: '50%', width: 32, height: 32,
                       cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <FaTimes />
      </button>
    </div>
    <img
      src={src} alt={name}
      onClick={e => e.stopPropagation()}
      style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 10,
               objectFit: 'contain', boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
    />
    <button
      onClick={e => { e.stopPropagation(); downloadBlob(src, name); }}
      style={{ marginTop: 14, background: 'white', color: '#111', border: 'none',
               borderRadius: 8, padding: '8px 18px', fontWeight: 700, fontSize: 13,
               cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
    >
      <FaDownload /> Download
    </button>
  </div>
);

export default FileAttachment;
